'use client';

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const el = document.getElementById(href.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      setMenuOpen(false);
    } else {
      setMenuOpen(false);
    }
  };

  const navItems = [
    { label: 'Properties', href: '/properties' },
    { label: 'Disciplines', href: '/disciplines' },
    { label: 'Services', href: '#services' },
    { label: 'Founder', href: '#founder' },
    { label: 'Team', href: '#team' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-12 py-5 transition-all duration-500 ${
          scrolled
            ? 'bg-[#1A0E08]/95 backdrop-blur-md border-b border-[#C4933A]/15'
            : 'bg-transparent'
        }`}
      >
        <a href="#top" onClick={(e) => handleNavClick(e as any, '#top')} className="group flex flex-col leading-none">
          <span className="font-display text-lg font-light tracking-[0.2em] text-[#F2EDE6] group-hover:text-[#D4A84B] transition-colors duration-300">
            Key Privé
          </span>
          <span className="font-body text-[8px] tracking-[0.4em] uppercase text-[#C4933A]/70 mt-0.5">
            Private Office
          </span>
        </a>

        <div className="flex items-center gap-4">
          {user && (
            <Link
              href="/dashboard"
              className="text-[9px] tracking-[0.25em] uppercase font-light text-[#C4933A]/70 hover:text-[#D4A84B] transition-colors duration-300 hidden md:block"
            >
              Dashboard
            </Link>
          )}
          <button
            className="relative w-10 h-10 flex flex-col items-center justify-center gap-1.5 group"
            aria-label="Open menu"
            onClick={() => setMenuOpen(true)}
          >
            <span className="block w-6 h-px bg-[#F2EDE6]/80 group-hover:bg-[#D4A84B] transition-all duration-300 group-hover:w-7" />
            <span className="block w-4 h-px bg-[#C4933A] group-hover:bg-[#D4A84B] transition-all duration-300 group-hover:w-7" />
            <span className="block w-6 h-px bg-[#F2EDE6]/80 group-hover:bg-[#D4A84B] transition-all duration-300 group-hover:w-7" />
          </button>
        </div>
      </nav>

      {/* Full-screen overlay menu */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-[200] flex flex-col"
          style={{ background: 'linear-gradient(135deg, #1A0E08 0%, #3C1B0D 60%, #2A1208 100%)' }}
        >
          {/* Background image */}
          <div className="absolute inset-0 opacity-20">
            <img
              src="/assets/images/WhatsApp_Image_2026-07-21_at_17.21.28-1785832245808.jpeg"
              alt=""
              aria-hidden="true"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#1A0E08]/60 to-[#1A0E08]/80" />

          {/* Gold corner accents */}
          <div className="absolute top-0 left-0 w-24 h-24 pointer-events-none">
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-[#C4933A] to-transparent opacity-50" />
            <div className="absolute top-0 left-0 h-full w-px bg-gradient-to-b from-[#C4933A] to-transparent opacity-50" />
          </div>
          <div className="absolute bottom-0 right-0 w-24 h-24 pointer-events-none">
            <div className="absolute bottom-0 right-0 w-full h-px bg-gradient-to-l from-[#C4933A] to-transparent opacity-50" />
            <div className="absolute bottom-0 right-0 h-full w-px bg-gradient-to-t from-[#C4933A] to-transparent opacity-50" />
          </div>

          {/* Close button */}
          <button
            className="absolute top-6 right-6 z-10 w-10 h-10 flex items-center justify-center text-[#F2EDE6]/70 hover:text-[#D4A84B] transition-colors duration-300"
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          {/* Menu content */}
          <div className="relative flex flex-col items-center justify-center flex-1 gap-1 px-8">
            {/* KP monogram */}
            <div className="mb-10 opacity-30">
              <span className="font-display text-8xl font-light text-[#C4933A]">KP</span>
            </div>

            {navItems.map((item, i) => (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="group relative font-display text-3xl md:text-5xl font-light text-[#F2EDE6]/80 hover:text-[#D4A84B] transition-all duration-400 py-3 px-8 text-center"
                style={{ transitionDelay: `${i * 50}ms` }}
              >
                <span className="relative">
                  {item.label}
                  <span className="absolute bottom-0 left-0 h-px w-0 group-hover:w-full transition-all duration-400"
                    style={{ background: 'linear-gradient(90deg, transparent, #C4933A, transparent)' }} />
                </span>
              </a>
            ))}

            {/* Dashboard link for authenticated users */}
            {user && (
              <Link
                href="/dashboard"
                onClick={() => setMenuOpen(false)}
                className="group relative font-display text-3xl md:text-5xl font-light text-[#C4933A]/70 hover:text-[#D4A84B] transition-all duration-400 py-3 px-8 text-center"
                style={{ transitionDelay: `${navItems.length * 50}ms` }}
              >
                <span className="relative">
                  Dashboard
                  <span className="absolute bottom-0 left-0 h-px w-0 group-hover:w-full transition-all duration-400"
                    style={{ background: 'linear-gradient(90deg, transparent, #C4933A, transparent)' }} />
                </span>
              </Link>
            )}

            {/* Gold divider */}
            <div className="mt-10 w-24 h-px" style={{ background: 'linear-gradient(90deg, transparent, #C4933A, transparent)' }} />
            <p className="font-body text-[10px] tracking-[0.4em] uppercase text-[#C4933A]/50 mt-4">
              London · International
            </p>
          </div>
        </div>
      )}
    </>
  );
}
